import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import {
  Store, ExternalLink, Copy, Check, MessageCircle, Save,
  AlertCircle, Loader2, Palette, Truck, Package as PackageIcon,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth'

interface LojaConfig {
  slug: string
  modulo_ativo: boolean
  nome_fantasia: string | null
  descricao: string | null
  cor_primaria: string | null
  whatsapp: string | null
  permite_retirada: boolean
  permite_entrega: boolean
  taxa_entrega_fixa: number | null
  chave_pix: string | null
  chave_pix_nome: string | null
  tipo_operacao: string
  endereco: {
    logradouro: string | null
    numero: string | null
    bairro: string | null
    cidade: string | null
    uf: string | null
  } | null
}

export default function MinhaLoja() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const isAdmin = user?.perfil === 'admin' || user?.perfil === 'super_admin'

  const { data: cfg, isLoading } = useQuery<LojaConfig>({
    queryKey: ['loja-admin-config'],
    queryFn: async () => (await api.get('/loja-admin/config')).data,
  })

  // Form state - só editável por admin
  const [form, setForm] = useState<Partial<LojaConfig>>({})
  useEffect(() => {
    if (cfg) {
      setForm({
        nome_fantasia: cfg.nome_fantasia,
        descricao: cfg.descricao,
        cor_primaria: cfg.cor_primaria,
        whatsapp: cfg.whatsapp,
        permite_retirada: cfg.permite_retirada,
        permite_entrega: cfg.permite_entrega,
        taxa_entrega_fixa: cfg.taxa_entrega_fixa,
        chave_pix: cfg.chave_pix,
        chave_pix_nome: cfg.chave_pix_nome,
      })
    }
  }, [cfg])

  // URL da loja pública
  const lojaUrl = useMemo(() => {
    if (!cfg?.slug) return ''
    return `${window.location.origin}/loja/${cfg.slug}`
  }, [cfg?.slug])

  const [copied, setCopied] = useState(false)
  function copiar() {
    navigator.clipboard.writeText(lojaUrl)
    setCopied(true)
    toast.success('Link copiado!')
    setTimeout(() => setCopied(false), 2000)
  }

  const saveMut = useMutation({
    mutationFn: async (data: any) => (await api.put('/loja-admin/config', {
      loja_nome_fantasia: data.nome_fantasia,
      loja_descricao: data.descricao,
      loja_cor_primaria: data.cor_primaria,
      loja_whatsapp: data.whatsapp,
      loja_permite_retirada: data.permite_retirada,
      loja_permite_entrega: data.permite_entrega,
      loja_taxa_entrega_fixa: data.taxa_entrega_fixa,
      loja_chave_pix: data.chave_pix,
      loja_chave_pix_nome: data.chave_pix_nome,
    })).data,
    onSuccess: () => {
      toast.success('Configurações salvas')
      qc.invalidateQueries({ queryKey: ['loja-admin-config'] })
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar')
    },
  })

  const whatsappMensagem = useMemo(() => {
    const nome = cfg?.nome_fantasia || 'nossa loja'
    return `Confira ${nome} online! ${lojaUrl}`
  }, [cfg?.nome_fantasia, lojaUrl])

  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMensagem)}`

  if (isLoading) {
    return (
      <div className="p-6">
        <Loader2 className="animate-spin text-ink-400" size={24} />
      </div>
    )
  }

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-ink-900 flex items-center gap-2">
          <Store size={24} /> Minha Loja Online
        </h1>
        <p className="text-sm text-ink-600 mt-1">
          Configure e compartilhe o link da sua loja com clientes.
        </p>
      </div>

      {!cfg?.modulo_ativo && (
        <div className="card card-body bg-amber-50 border-amber-300 flex items-start gap-3">
          <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-amber-900">
            O módulo <strong>Loja Online</strong> está desativado para esta empresa.
            Fale com o suporte para ativar.
          </div>
        </div>
      )}

      {/* CARD PRINCIPAL — URL + QR */}
      <div className="card card-body">
        <div className="label mb-1">URL pública da sua loja</div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 md:gap-6 items-start">
          {/* Esquerda: URL + botões */}
          <div className="min-w-0 space-y-3">
            <div className="flex items-center gap-2 bg-ink-50 border border-ink-200 rounded-md p-3">
              <ExternalLink size={16} className="text-brand-600 shrink-0" />
              <div className="font-mono text-sm text-ink-900 truncate">{lojaUrl}</div>
            </div>

            <div className="flex flex-wrap gap-2">
              <a
                href={lojaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-sm"
              >
                <ExternalLink size={14} /> Abrir loja
              </a>
              <button type="button" onClick={copiar} className="btn-ghost text-sm">
                {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                {copied ? 'Copiado' : 'Copiar link'}
              </button>
              <a
                href={whatsappShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost text-sm"
              >
                <MessageCircle size={14} className="text-emerald-600" />
                Compartilhar no WhatsApp
              </a>
            </div>

            <div className="text-xs text-ink-500">
              Compartilhe este link com seus clientes. Eles poderão navegar pelo catálogo,
              cadastrar-se e fazer pedidos.
            </div>
          </div>

          {/* Direita: QR */}
          {lojaUrl && (
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div className="p-3 bg-white border-2 border-ink-200 rounded-lg">
                <QRCodeSVG value={lojaUrl} size={140} bgColor="#ffffff" fgColor="#111827" />
              </div>
              <div className="text-[10px] text-ink-500 uppercase tracking-wider">
                QR code da loja
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CARD CONFIG */}
      <div className="card card-body space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-ink-900">Configurações da loja</h2>
          <p className="text-xs text-ink-500 mt-0.5">
            {isAdmin
              ? 'Ajuste os dados que aparecem para seus clientes.'
              : 'Apenas administradores podem alterar essas configurações.'}
          </p>
        </div>

        <fieldset disabled={!isAdmin || saveMut.isPending} className="space-y-4">
          {/* Nome */}
          <div>
            <label className="label">Nome da loja</label>
            <input
              className="input"
              placeholder="Ex: Mercado do Seu Zé"
              value={form.nome_fantasia || ''}
              onChange={e => setForm(f => ({ ...f, nome_fantasia: e.target.value }))}
            />
            <p className="text-xs text-ink-500 mt-1">
              Aparece no topo da loja pública. Deixe em branco para usar o nome fantasia cadastrado da empresa.
            </p>
          </div>

          {/* Descrição */}
          <div>
            <label className="label">Descrição</label>
            <textarea
              className="input min-h-[72px]"
              placeholder="Ex: Produtos frescos, entrega rápida na sua casa..."
              value={form.descricao || ''}
              onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
            />
          </div>

          {/* WhatsApp */}
          <div>
            <label className="label flex items-center gap-1">
              <MessageCircle size={14} /> WhatsApp de contato
            </label>
            <input
              className="input"
              placeholder="(88) 99999-8888"
              value={form.whatsapp || ''}
              onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))}
            />
            <p className="text-xs text-ink-500 mt-1">
              Aparece como botão de atendimento direto.
            </p>
          </div>

          {/* PIX */}
          <div className="border border-ink-200 rounded-md p-3 bg-ink-50 space-y-3">
            <div className="text-sm font-semibold text-ink-900 flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M16 2 2 16l14 14 14-14zm0 4.24L25.76 16 16 25.76 6.24 16z"/></svg>
              Chave PIX (para recebimento na loja)
            </div>
            <div>
              <label className="label">Chave PIX</label>
              <input
                className="input"
                placeholder="CPF, e-mail, telefone ou chave aleatória"
                value={form.chave_pix || ''}
                onChange={e => setForm(f => ({ ...f, chave_pix: e.target.value }))}
              />
              <p className="text-xs text-ink-500 mt-1">
                Será mostrada ao cliente quando ele escolher PIX como forma de pagamento.
              </p>
            </div>
            <div>
              <label className="label">Nome do titular (opcional)</label>
              <input
                className="input"
                placeholder="Nome que aparece na conta do PIX"
                value={form.chave_pix_nome || ''}
                onChange={e => setForm(f => ({ ...f, chave_pix_nome: e.target.value }))}
              />
            </div>
          </div>

          {/* Cor primaria */}
          <div>
            <label className="label flex items-center gap-1">
              <Palette size={14} /> Cor primária (futura personalização)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                className="h-10 w-14 border border-ink-200 rounded cursor-pointer"
                value={form.cor_primaria || '#2563eb'}
                onChange={e => setForm(f => ({ ...f, cor_primaria: e.target.value }))}
              />
              <input
                className="input flex-1 font-mono"
                placeholder="#2563eb"
                value={form.cor_primaria || ''}
                onChange={e => setForm(f => ({ ...f, cor_primaria: e.target.value }))}
              />
            </div>
          </div>

          {/* Retirada / entrega */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-start gap-2 p-3 border border-ink-200 rounded-md cursor-pointer hover:bg-ink-50">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!form.permite_retirada}
                onChange={e => setForm(f => ({ ...f, permite_retirada: e.target.checked }))}
              />
              <div>
                <div className="flex items-center gap-1.5 text-sm font-medium">
                  <PackageIcon size={14} /> Permitir retirada na loja
                </div>
                <div className="text-xs text-ink-500 mt-0.5">
                  Cliente escolhe buscar o pedido pessoalmente.
                </div>
              </div>
            </label>

            <label className="flex items-start gap-2 p-3 border border-ink-200 rounded-md cursor-pointer hover:bg-ink-50">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!form.permite_entrega}
                onChange={e => setForm(f => ({ ...f, permite_entrega: e.target.checked }))}
              />
              <div>
                <div className="flex items-center gap-1.5 text-sm font-medium">
                  <Truck size={14} /> Permitir entrega
                </div>
                <div className="text-xs text-ink-500 mt-0.5">
                  Cliente informa endereço e paga taxa.
                </div>
              </div>
            </label>
          </div>

          {/* Taxa fixa de entrega */}
          {form.permite_entrega && (
            <div>
              <label className="label">Taxa fixa de entrega (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="input max-w-[200px]"
                placeholder="0,00"
                value={form.taxa_entrega_fixa ?? ''}
                onChange={e => setForm(f => ({
                  ...f,
                  taxa_entrega_fixa: e.target.value === '' ? null : Number(e.target.value),
                }))}
              />
              <p className="text-xs text-ink-500 mt-1">
                Deixe em branco para informar "A combinar".
              </p>
            </div>
          )}
        </fieldset>

        {isAdmin && (
          <div className="flex justify-end pt-2 border-t border-ink-100">
            <button
              type="button"
              onClick={() => saveMut.mutate(form)}
              disabled={saveMut.isPending}
              className="btn-primary"
            >
              {saveMut.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Salvando...</>
                : <><Save size={14} /> Salvar configurações</>}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
